import { useState } from 'react'
import { toast } from 'sonner'
import {
  useIntegrations,
  useIntegrationTypes,
  useCreateIntegration,
  useDeleteIntegration,
  useTestIntegration,
  useUpdateIntegration,
} from '@/hooks/useApi'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import {
  Plus,
  Trash2,
  Play,
  CheckCircle,
  XCircle,
  Settings,
  Power,
  HelpCircle,
  X,
  Save,
} from 'lucide-react'
import { relatief } from '@/lib/datum'

// ─── Config field definitions per integration type ───

interface ConfigField {
  key: string
  label: string
  type: 'text' | 'password' | 'email' | 'url'
  required: boolean
  placeholder: string
  help: string
}

const CONFIG_FIELDS: Record<string, ConfigField[]> = {
  woocommerce_events: [
    {
      key: 'store_url',
      label: 'Store URL',
      type: 'url',
      required: true,
      placeholder: 'https://jouw-winkel.be',
      help: 'De URL van je WooCommerce webshop (zonder /wp-json). Bijvoorbeeld: https://mac-a-dram.be',
    },
    {
      key: 'consumer_key',
      label: 'Consumer Key',
      type: 'password',
      required: true,
      placeholder: 'ck_...',
      help: 'WooCommerce REST API Consumer Key. Maak aan via WooCommerce > Settings > Advanced > REST API. Kies "Read" rechten.',
    },
    {
      key: 'consumer_secret',
      label: 'Consumer Secret',
      type: 'password',
      required: true,
      placeholder: 'cs_...',
      help: 'WooCommerce REST API Consumer Secret. Wordt samen met de Consumer Key aangemaakt.',
    },
    {
      key: 'invoice_email',
      label: 'Factuur e-mail',
      type: 'email',
      required: true,
      placeholder: 'facturen@voorbeeld.be',
      help: 'E-mailadres waar de factuuroverzichten naartoe worden gestuurd na elk event.',
    },
    {
      key: 'invoice_cc',
      label: 'CC e-mail(s)',
      type: 'text',
      required: false,
      placeholder: 'cc@voorbeeld.be, extra@voorbeeld.be',
      help: 'Optioneel: extra e-mailadressen die een kopie ontvangen. Meerdere adressen scheiden met komma.',
    },
    {
      key: 'timezone',
      label: 'Tijdzone',
      type: 'text',
      required: false,
      placeholder: 'Europe/Amsterdam',
      help: 'Tijdzone voor event datums. Standaard: Europe/Amsterdam.',
    },
    {
      key: 'bridge_url',
      label: 'Bridge URL',
      type: 'url',
      required: false,
      placeholder: 'https://jouw-winkel.be/wp-json/techvibe/v1/wc-proxy',
      help: 'Optioneel: URL van het Techvibe Bridge mu-plugin. Gebruik dit als de hosting (bijv. SiteGround) het server IP blokkeert met een captcha. Upload techvibe-bridge.php naar wp-content/mu-plugins/ en vul deze URL in.',
    },
    {
      key: 'bridge_api_key',
      label: 'Bridge API Key',
      type: 'password',
      required: false,
      placeholder: 'tvb_...',
      help: 'API key voor het bridge endpoint. Na installatie van het mu-plugin: ga naar /wp-json/techvibe/v1/bridge-key (ingelogd als admin) om de key op te halen.',
    },
  ],
  azure_health: [
    {
      key: 'tenant_id',
      label: 'Tenant ID',
      type: 'text',
      required: true,
      placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      help: 'Azure Active Directory Tenant ID. Te vinden in Azure Portal > Azure AD > Overview.',
    },
    {
      key: 'client_id',
      label: 'Client ID',
      type: 'text',
      required: true,
      placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      help: 'Application (client) ID van de Azure AD App Registration.',
    },
    {
      key: 'client_secret',
      label: 'Client Secret',
      type: 'password',
      required: true,
      placeholder: '',
      help: 'Client secret van de Azure AD App Registration. Maak aan via Certificates & secrets.',
    },
    {
      key: 'subscription_id',
      label: 'Subscription ID',
      type: 'text',
      required: false,
      placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      help: 'Azure Subscription ID om te monitoren.',
    },
  ],
}

// ─── Help Tooltip Component ───

function HelpTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false)

  return (
    <span className="relative inline-block">
      <HelpCircle
        className="ml-1 inline h-3.5 w-3.5 cursor-help text-muted-foreground hover:text-foreground"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
      />
      {show && (
        <span className="absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
          {text}
        </span>
      )}
    </span>
  )
}

// ─── Config Form Component ───

function ConfigFields({
  type,
  config,
  onChange,
}: {
  type: string
  config: Record<string, string>
  onChange: (config: Record<string, string>) => void
}) {
  const fields = CONFIG_FIELDS[type]
  if (!fields) return null

  return (
    <div className="mt-4 space-y-3 border-t pt-4">
      <p className="text-sm font-medium text-muted-foreground">Configuratie</p>
      <div className="grid gap-3 md:grid-cols-2">
        {fields.map((field) => (
          <div key={field.key}>
            <label className="flex items-center text-sm font-medium">
              {field.label}
              {field.required && <span className="ml-0.5 text-destructive">*</span>}
              <HelpTooltip text={field.help} />
            </label>
            <Input
              type={field.type}
              className="mt-1"
              value={config[field.key] || ''}
              onChange={(e) => onChange({ ...config, [field.key]: e.target.value })}
              placeholder={field.placeholder}
              required={field.required}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Main Component ───

export default function Integrations() {
  const { data: integrations, isLoading } = useIntegrations()
  const { data: types } = useIntegrationTypes()
  const createIntegration = useCreateIntegration()
  const deleteIntegration = useDeleteIntegration()
  const testIntegration = useTestIntegration()
  const updateIntegration = useUpdateIntegration()

  const [showCreate, setShowCreate] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editConfig, setEditConfig] = useState<Record<string, string>>({})
  const [newIntegration, setNewIntegration] = useState({
    name: '',
    type: '',
    description: '',
  })
  const [newConfig, setNewConfig] = useState<Record<string, string>>({})
  const [testResult, setTestResult] = useState<{ id: number; success: boolean; message: string } | null>(null)

  const handleCreate = async () => {
    if (!newIntegration.name || !newIntegration.type) return

    // Validate required config fields
    const fields = CONFIG_FIELDS[newIntegration.type]
    if (fields) {
      const missing = fields.filter((f) => f.required && !newConfig[f.key])
      if (missing.length > 0) {
        toast.warning('Verplichte velden ontbreken', {
          description: missing.map((f) => f.label).join(', '),
        })
        return
      }
    }

    // Convert invoice_cc from comma-separated string to array
    const config: Record<string, unknown> = { ...newConfig }
    if (config.invoice_cc && typeof config.invoice_cc === 'string') {
      config.invoice_cc = (config.invoice_cc as string)
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean)
    }

    try {
      await createIntegration.mutateAsync({
        name: newIntegration.name,
        type: newIntegration.type,
        description: newIntegration.description || undefined,
        config,
      })
      toast.success('Integratie aangemaakt')
      setNewIntegration({ name: '', type: '', description: '' })
      setNewConfig({})
      setShowCreate(false)
    } catch (err) {
      toast.error('Fout bij aanmaken', {
        description: err instanceof Error ? err.message : 'Onbekende fout',
      })
    }
  }

  const handleToggle = async (id: number, enabled: boolean) => {
    try {
      await updateIntegration.mutateAsync({
        id,
        data: { enabled: !enabled },
      })
      toast.success(enabled ? 'Integratie uitgeschakeld' : 'Integratie ingeschakeld')
    } catch (err) {
      toast.error('Fout bij wijzigen', {
        description: err instanceof Error ? err.message : 'Onbekende fout',
      })
    }
  }

  const handleTest = async (id: number) => {
    setTestResult(null)
    try {
      const result = await testIntegration.mutateAsync(id)
      setTestResult({ id, success: result.success, message: result.message })
      if (result.success) {
        toast.success('Verbinding geslaagd')
      } else {
        toast.error('Verbinding mislukt', { description: result.message })
      }
      setTimeout(() => setTestResult(null), 5000)
    } catch (err) {
      toast.error('Test mislukt', {
        description: err instanceof Error ? err.message : 'Onbekende fout',
      })
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteIntegration.mutateAsync(id)
      toast.success('Integratie verwijderd')
    } catch (err) {
      toast.error('Fout bij verwijderen', {
        description: err instanceof Error ? err.message : 'Onbekende fout',
      })
    }
  }

  const handleEditConfig = (integration: { id: number; config: Record<string, unknown> }) => {
    if (editingId === integration.id) {
      setEditingId(null)
      return
    }
    // Flatten config to strings for editing
    const flat: Record<string, string> = {}
    for (const [key, value] of Object.entries(integration.config || {})) {
      flat[key] = Array.isArray(value) ? value.join(', ') : String(value ?? '')
    }
    setEditConfig(flat)
    setEditingId(integration.id)
  }

  const handleSaveConfig = async (id: number, _type: string) => {
    const config: Record<string, unknown> = { ...editConfig }
    // Convert invoice_cc to array
    if (config.invoice_cc && typeof config.invoice_cc === 'string') {
      config.invoice_cc = (config.invoice_cc as string)
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean)
    }

    try {
      await updateIntegration.mutateAsync({ id, data: { config } })
      toast.success('Configuratie opgeslagen')
      setEditingId(null)
    } catch (err) {
      toast.error('Fout bij opslaan', {
        description: err instanceof Error ? err.message : 'Onbekende fout',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Integraties</h1>
          <p className="text-muted-foreground">
            Beheer je externe systeem-koppelingen
          </p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)}>
          <Plus className="mr-2 h-4 w-4" />
          Toevoegen
        </Button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Nieuwe integratie</CardTitle>
            <Button size="icon" variant="ghost" onClick={() => setShowCreate(false)}>
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="flex items-center text-sm font-medium">
                  Naam
                  <HelpTooltip text="Een herkenbare naam voor deze integratie, bijv. 'Mac-a-Dram WooCommerce'" />
                </label>
                <Input
                  className="mt-1"
                  value={newIntegration.name}
                  onChange={(e) =>
                    setNewIntegration({ ...newIntegration, name: e.target.value })
                  }
                  placeholder="Bijv. Mac-a-Dram Events"
                />
              </div>
              <div>
                <label className="flex items-center text-sm font-medium">
                  Type
                  <HelpTooltip text="Het type integratie bepaalt welke configuratie-velden je moet invullen." />
                </label>
                <Select
                  className="mt-1"
                  value={newIntegration.type}
                  onChange={(e) => {
                    setNewIntegration({ ...newIntegration, type: e.target.value })
                    setNewConfig({}) // Reset config when type changes
                  }}
                >
                  <option value="">Selecteer type...</option>
                  {types?.map((type) => (
                    <option key={type.type} value={type.type}>
                      {type.type === 'woocommerce_events'
                        ? 'WooCommerce Events'
                        : type.type === 'azure_health'
                        ? 'Azure Health Monitor'
                        : type.type}{' '}
                      — {type.description}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="flex items-center text-sm font-medium">
                  Beschrijving
                  <HelpTooltip text="Optionele beschrijving, bijv. 'Dagelijkse factuuroverzichten voor whisky tastings'" />
                </label>
                <Input
                  className="mt-1"
                  value={newIntegration.description}
                  onChange={(e) =>
                    setNewIntegration({
                      ...newIntegration,
                      description: e.target.value,
                    })
                  }
                  placeholder="Optionele beschrijving"
                />
              </div>
            </div>

            {/* Type-specific config fields */}
            {newIntegration.type && (
              <ConfigFields
                type={newIntegration.type}
                config={newConfig}
                onChange={setNewConfig}
              />
            )}

            <div className="mt-4 flex gap-2">
              <Button onClick={handleCreate} disabled={createIntegration.isPending}>
                {createIntegration.isPending ? (
                  <Spinner size="sm" className="mr-2" />
                ) : null}
                Aanmaken
              </Button>
              <Button variant="outline" onClick={() => setShowCreate(false)}>
                Annuleren
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Integrations List */}
      <div className="space-y-4">
        {integrations?.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">Nog geen integraties geconfigureerd</p>
            </CardContent>
          </Card>
        ) : (
          integrations?.map((integration) => (
            <Card key={integration.id}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className={`h-3 w-3 rounded-full ${
                        integration.enabled
                          ? integration.last_sync_status === 'success'
                            ? 'bg-green-500'
                            : integration.last_sync_status === 'failed'
                            ? 'bg-red-500'
                            : 'bg-yellow-500'
                          : 'bg-gray-400'
                      }`}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{integration.name}</span>
                        <Badge variant="outline">
                          {integration.type === 'woocommerce_events'
                            ? 'WooCommerce'
                            : integration.type === 'azure_health'
                            ? 'Azure'
                            : integration.type}
                        </Badge>
                        {!integration.enabled && (
                          <Badge variant="secondary">Uitgeschakeld</Badge>
                        )}
                      </div>
                      {integration.description && (
                        <p className="text-sm text-muted-foreground">
                          {integration.description}
                        </p>
                      )}
                      <div className="mt-1 flex items-center gap-4 text-xs text-muted-foreground">
                        {integration.last_sync_at && (
                          <span>
                            Laatste sync:{' '}
                            {relatief(integration.last_sync_at)}
                          </span>
                        )}
                        {integration.last_sync_status && (
                          <span className="flex items-center gap-1">
                            {integration.last_sync_status === 'success' ? (
                              <CheckCircle className="h-3 w-3 text-green-500" />
                            ) : (
                              <XCircle className="h-3 w-3 text-red-500" />
                            )}
                            {integration.last_sync_status}
                          </span>
                        )}
                      </div>
                      {integration.last_error && (
                        <p className="mt-1 text-xs text-destructive">
                          {integration.last_error}
                        </p>
                      )}
                      {/* Test result */}
                      {testResult?.id === integration.id && (
                        <p
                          className={`mt-1 text-xs ${
                            testResult.success ? 'text-green-600 dark:text-green-300' : 'text-destructive'
                          }`}
                        >
                          {testResult.success ? 'Verbinding geslaagd' : `Verbinding mislukt: ${testResult.message}`}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTest(integration.id)}
                      disabled={testIntegration.isPending}
                      title="Test verbinding"
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        handleToggle(integration.id, integration.enabled)
                      }
                      title={integration.enabled ? 'Uitschakelen' : 'Inschakelen'}
                    >
                      <Power
                        className={`h-4 w-4 ${
                          integration.enabled ? 'text-green-500' : 'text-gray-400'
                        }`}
                      />
                    </Button>
                    <Button
                      size="sm"
                      variant={editingId === integration.id ? 'default' : 'outline'}
                      onClick={() => handleEditConfig(integration)}
                      title="Configuratie bewerken"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        toast('Deze integratie verwijderen?', {
                          action: {
                            label: 'Verwijderen',
                            onClick: () => handleDelete(integration.id),
                          },
                        })
                      }}
                      title="Verwijderen"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>

                {/* Editable config panel */}
                {editingId === integration.id && (
                  <div className="mt-4 border-t pt-4">
                    {CONFIG_FIELDS[integration.type] ? (
                      <>
                        <ConfigFields
                          type={integration.type}
                          config={editConfig}
                          onChange={setEditConfig}
                        />
                        <div className="mt-4 flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveConfig(integration.id, integration.type)}
                            disabled={updateIntegration.isPending}
                          >
                            {updateIntegration.isPending ? (
                              <Spinner size="sm" className="mr-2" />
                            ) : (
                              <Save className="mr-2 h-4 w-4" />
                            )}
                            Opslaan
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingId(null)}
                          >
                            Annuleren
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Geen configuratie-velden beschikbaar voor dit type.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}
